/** Geocoding: Nominatim first, US Census second (§12.3), both schema-validated.
 *  Divergence from the prototype, argued at Gate 1/§8: the Census fallback uses CORS fetch,
 *  not JSONP — JSONP executes remote script, which the security requirements forbid.
 *  If both fail, the manual path is stated: click the map and use "Pin site here". */
import { z } from 'zod';
import { fetchJson, ServiceError } from '../core/net';

export interface GeocodeHit { lat: number; lng: number; display: string; source: 'nominatim' | 'census' }

const NominatimSchema = z.array(z.object({
  lat: z.coerce.number(), lon: z.coerce.number(), display_name: z.string(),
})).max(50);

const CensusSchema = z.object({
  result: z.object({
    addressMatches: z.array(z.object({
      coordinates: z.object({ x: z.number(), y: z.number() }),
      matchedAddress: z.string(),
    })),
  }),
});

/** Phoenix metro box — results inside it sort first, but nothing outside is excluded. */
const METRO: [number, number, number, number] = [-113.4, 34.1, -111.0, 32.4]; // left,top,right,bottom

/**
 * Several candidates, best-first — the old version took the single first hit and
 * threw otherwise, which is why anything less than an exact address failed (field
 * feedback 2026-07-25). Partial text and cross-streets now come back as a list.
 */
export async function geocodeMany(query: string, limit = 8): Promise<GeocodeHit[]> {
  const q = query.trim();
  if (!q) return [];
  try {
    const params = new URLSearchParams({
      format: 'jsonv2', limit: String(limit), countrycodes: 'us',
      viewbox: METRO.join(','), q,
    });
    const raw = await fetchJson('Nominatim', `https://nominatim.openstreetmap.org/search?${params.toString()}`);
    const parsed = NominatimSchema.safeParse(raw);
    if (!parsed.success) throw new ServiceError('Nominatim', 'malformed', 'The address service answered in an unexpected shape.');
    const inMetro = (h: { lat: number; lon: number }) =>
      h.lon >= METRO[0] && h.lon <= METRO[2] && h.lat <= METRO[1] && h.lat >= METRO[3];
    const hits = parsed.data
      .map((h) => ({ lat: h.lat, lng: h.lon, display: h.display_name, source: 'nominatim' as const }))
      .sort((a, b) => Number(inMetro({ lat: b.lat, lon: b.lng })) - Number(inMetro({ lat: a.lat, lon: a.lng })));
    if (hits.length) return hits;
  } catch {
    // Nominatim down or malformed — the Census geocoder still handles exact addresses (§12.3)
  }
  return censusHits(q);
}

/** Census only matches complete street addresses, so it returns at most one hit. */
async function censusHits(q: string): Promise<GeocodeHit[]> {
  const raw = await fetchJson('US Census geocoder',
    `https://geocoding.geo.census.gov/geocoder/locations/onelineaddress?benchmark=Public_AR_Current&format=json&address=${encodeURIComponent(q)}`);
  const parsed = CensusSchema.safeParse(raw);
  if (!parsed.success) return [];
  return parsed.data.result.addressMatches
    .slice(0, 1)
    .map((m) => ({ lat: m.coordinates.y, lng: m.coordinates.x, display: m.matchedAddress, source: 'census' as const }));
}

export async function geocode(query: string): Promise<GeocodeHit> {
  const q = query.trim();
  if (!q) throw new ServiceError('Geocoding', 'guard', 'Type an address first.');
  try {
    const hits = await geocodeMany(q, 1);
    const hit = hits[0];
    if (!hit) throw new ServiceError('Nominatim', 'empty', 'No match for that address.');
    return hit;
  } catch (e) {
    if (e instanceof ServiceError && e.kind === 'empty') throw e; // a clean no-match is an answer, not an outage
    // fall through to Census on outage/malformed
  }
  const raw = await fetchJson('US Census geocoder',
    `https://geocoding.geo.census.gov/geocoder/locations/onelineaddress?benchmark=Public_AR_Current&format=json&address=${encodeURIComponent(q)}`);
  const parsed = CensusSchema.safeParse(raw);
  if (!parsed.success) throw new ServiceError('US Census geocoder', 'malformed', 'The Census geocoder answered in an unexpected shape.');
  const m = parsed.data.result.addressMatches[0];
  if (!m) throw new ServiceError('Geocoding', 'empty', 'No match for that address.');
  return { lat: m.coordinates.y, lng: m.coordinates.x, display: m.matchedAddress, source: 'census' };
}
